// A minimal, STRICT, canonical CBOR codec scoped to the ADR-0061 v2 envelope
// header grammar. Not a general-purpose CBOR implementation — deliberately.
//
// Why hand-rolled rather than a dependency: this lives in `@augmented/core`, the
// crown-jewel crypto package bundled into the CLI and ~14 Lambdas, whose
// dependency set is kept deliberately tiny. The header is a fixed-shape map of
// short strings, so the codec is small and exhaustively testable, and we get to
// enforce the two properties a general library often will NOT enforce on DECODE:
// canonical (deterministic) encoding and duplicate-key rejection.
//
// Canonical here means RFC 8949 §4.2.1 "core deterministic encoding":
//   - definite-length items only (no indefinite length)
//   - integers/lengths in the SHORTEST form
//   - map keys sorted by their encoded bytes, bytewise-lexicographic, and unique
//
// This matters because the canonical header bytes are authenticated as part of
// the GCM AAD (ADR-0061 Part 1b). A given logical header must always serialise to
// the same bytes, and a decoder must reject any non-canonical variant so a
// tampered header cannot masquerade as an equivalent one.
//
// Supported value space: non-negative integers (< 2^32), text strings, arrays,
// and string-keyed maps. Every other CBOR construct (negative ints, byte
// strings, tags, floats/simple values, indefinite lengths) is rejected — the
// grammar never needs them, and refusing them shrinks the attack surface.

/** The JS value space this codec round-trips. */
export type CborValue = number | string | CborValue[] | { [key: string]: CborValue };

const MAJOR_UINT = 0;
const MAJOR_TSTR = 3;
const MAJOR_ARRAY = 4;
const MAJOR_MAP = 5;

const MAX_UINT = 0xff_ff_ff_ff; // 2^32 - 1; the largest value/length we encode

/** Encode a major type + argument (a uint value or an item length) in shortest form. */
function encodeHead(major: number, arg: number): Buffer {
  if (!Number.isInteger(arg) || arg < 0 || arg > MAX_UINT) {
    throw new RangeError(`cbor: argument out of range: ${arg}`);
  }
  const initial = major << 5;
  if (arg < 24) return Buffer.from([initial | arg]);
  if (arg < 0x100) return Buffer.from([initial | 24, arg]);
  if (arg < 0x1_0000) {
    const b = Buffer.alloc(3);
    b[0] = initial | 25;
    b.writeUInt16BE(arg, 1);
    return b;
  }
  const b = Buffer.alloc(5);
  b[0] = initial | 26;
  b.writeUInt32BE(arg, 1);
  return b;
}

/** Canonically encode a value. Throws on anything outside the supported space. */
export function encodeCanonicalCbor(value: CborValue): Buffer {
  if (typeof value === 'number') {
    if (!Number.isInteger(value) || value < 0) {
      throw new RangeError(`cbor: only non-negative integers are supported, got ${value}`);
    }
    return encodeHead(MAJOR_UINT, value);
  }

  if (typeof value === 'string') {
    const bytes = Buffer.from(value, 'utf8');
    return Buffer.concat([encodeHead(MAJOR_TSTR, bytes.length), bytes]);
  }

  if (Array.isArray(value)) {
    const items = value.map((item) => encodeCanonicalCbor(item));
    return Buffer.concat([encodeHead(MAJOR_ARRAY, value.length), ...items]);
  }

  if (value !== null && typeof value === 'object') {
    const keys = Object.keys(value);
    // Sort ENTRIES by the encoded bytes of their (text-string) keys, so the
    // serialisation is deterministic regardless of JS property-insertion order.
    const entries = keys.map((key) => {
      const encodedKey = Buffer.concat([
        encodeHead(MAJOR_TSTR, Buffer.byteLength(key, 'utf8')),
        Buffer.from(key, 'utf8'),
      ]);
      return { encodedKey, encodedValue: encodeCanonicalCbor(value[key]!) };
    });
    entries.sort((a, b) => Buffer.compare(a.encodedKey, b.encodedKey));
    const body = entries.flatMap((e) => [e.encodedKey, e.encodedValue]);
    return Buffer.concat([encodeHead(MAJOR_MAP, keys.length), ...body]);
  }

  throw new TypeError(`cbor: unsupported value type: ${typeof value}`);
}

/** Thrown for any structurally-invalid or non-canonical input. */
export class CborDecodeError extends Error {}

interface Head {
  major: number;
  arg: number;
  next: number;
}

/** Read a head at `offset`, enforcing definite length + shortest-form argument. */
function readHead(buf: Buffer, offset: number): Head {
  if (offset >= buf.length) throw new CborDecodeError('cbor: truncated (expected head)');
  const first = buf[offset]!;
  const major = first >> 5;
  const ai = first & 0x1f;

  if (ai < 24) return { major, arg: ai, next: offset + 1 };

  if (ai === 24) {
    if (offset + 2 > buf.length) throw new CborDecodeError('cbor: truncated uint8 arg');
    const arg = buf[offset + 1]!;
    if (arg < 24) throw new CborDecodeError('cbor: non-canonical (uint8 < 24)');
    return { major, arg, next: offset + 2 };
  }
  if (ai === 25) {
    if (offset + 3 > buf.length) throw new CborDecodeError('cbor: truncated uint16 arg');
    const arg = buf.readUInt16BE(offset + 1);
    if (arg < 0x100) throw new CborDecodeError('cbor: non-canonical (uint16 < 256)');
    return { major, arg, next: offset + 3 };
  }
  if (ai === 26) {
    if (offset + 5 > buf.length) throw new CborDecodeError('cbor: truncated uint32 arg');
    const arg = buf.readUInt32BE(offset + 1);
    if (arg < 0x1_0000) throw new CborDecodeError('cbor: non-canonical (uint32 < 65536)');
    return { major, arg, next: offset + 5 };
  }
  // ai 27 (8-byte, unneeded), 28-30 (reserved), 31 (indefinite) are all rejected.
  throw new CborDecodeError(`cbor: unsupported additional-info ${ai}`);
}

interface Decoded {
  value: CborValue;
  next: number;
}

function decodeValue(buf: Buffer, offset: number): Decoded {
  const head = readHead(buf, offset);

  switch (head.major) {
    case MAJOR_UINT:
      return { value: head.arg, next: head.next };

    case MAJOR_TSTR: {
      const end = head.next + head.arg;
      if (end > buf.length) throw new CborDecodeError('cbor: truncated text string');
      const bytes = buf.subarray(head.next, end);
      const str = bytes.toString('utf8');
      // Reject invalid/non-canonical UTF-8: a lossy decode would not round-trip.
      if (!Buffer.from(str, 'utf8').equals(bytes)) {
        throw new CborDecodeError('cbor: invalid UTF-8 in text string');
      }
      return { value: str, next: end };
    }

    case MAJOR_ARRAY: {
      const arr: CborValue[] = [];
      let cursor = head.next;
      for (let i = 0; i < head.arg; i++) {
        const decoded = decodeValue(buf, cursor);
        arr.push(decoded.value);
        cursor = decoded.next;
      }
      return { value: arr, next: cursor };
    }

    case MAJOR_MAP: {
      const obj: { [key: string]: CborValue } = {};
      let cursor = head.next;
      let prevKeyBytes: Buffer | null = null;
      for (let i = 0; i < head.arg; i++) {
        const keyStart = cursor;
        const keyHead = readHead(buf, cursor);
        if (keyHead.major !== MAJOR_TSTR) {
          throw new CborDecodeError('cbor: map keys must be text strings');
        }
        const keyDecoded = decodeValue(buf, cursor);
        const keyBytes = buf.subarray(keyStart, keyDecoded.next);
        // Strictly increasing encoded-key bytes ⇒ canonical order AND no duplicates.
        if (prevKeyBytes !== null && Buffer.compare(keyBytes, prevKeyBytes) <= 0) {
          throw new CborDecodeError('cbor: map keys not in canonical order or duplicated');
        }
        prevKeyBytes = Buffer.from(keyBytes);
        const valDecoded = decodeValue(buf, keyDecoded.next);
        obj[keyDecoded.value as string] = valDecoded.value;
        cursor = valDecoded.next;
      }
      return { value: obj, next: cursor };
    }

    default:
      // Negative ints, byte strings, tags, floats/simple values.
      throw new CborDecodeError(`cbor: unsupported major type ${head.major}`);
  }
}

/**
 * Decode a canonical-CBOR buffer, requiring the WHOLE buffer to be consumed by a
 * single top-level item (trailing bytes are rejected).
 */
export function decodeCanonicalCbor(buf: Buffer): CborValue {
  const { value, next } = decodeValue(buf, 0);
  if (next !== buf.length) {
    throw new CborDecodeError(`cbor: ${buf.length - next} trailing byte(s) after top-level item`);
  }
  return value;
}
